import React, { useState } from 'react';
import { useMetronome } from '../contexts/MetronomeContext';
import { useLanguage } from '../contexts/LanguageContext';
import VerticalFader from './VerticalFader';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiVolume2, FiChevronDown, FiChevronUp } = FiIcons;

const VolumeControls = () => {
  const { volumeSettings, setVolumeSettings } = useMetronome();
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  // Convert the 0-1 scale to 0-100 for the fader
  const mainVolume = volumeSettings && volumeSettings.main !== undefined 
    ? Math.min(volumeSettings.main * 100, 100) 
    : 100;

  const handleMainVolumeChange = (newValue) => {
    // Convert back to 0-1 scale for the context
    setVolumeSettings(prev => ({
      ...prev,
      main: Math.min(newValue / 100, 1.0)
    }));
  };

  const handleVolumePresetClick = (preset) => {
    switch (preset) {
      case 'mute':
        handleMainVolumeChange(0);
        break;
      case 'medium':
        handleMainVolumeChange(50);
        break;
      case 'max':
        handleMainVolumeChange(100);
        break;
      default:
        break;
    }
  };

  return (
    <motion.div 
      className="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Mobile: Collapsible header */}
      <div className="block md:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiVolume2} className="w-5 h-5 text-primary-500 mr-3" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              {t('volumeControl')}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
              {Math.round(mainVolume)}%
            </span>
            <SafeIcon 
              icon={isExpanded ? FiChevronUp : FiChevronDown} 
              className="w-5 h-5 text-gray-500" 
            />
          </div>
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="p-4">
                {/* Mobile: Horizontal slider */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t('mainVolume')}
                    </span>
                    <span className="text-sm bg-gray-100 dark:bg-gray-700 rounded px-3 py-1 font-mono">
                      {Math.round(mainVolume)}%
                    </span>
                  </div>
                  
                  <input 
                    type="range" 
                    min="0" 
                    max="100" 
                    value={mainVolume}
                    onChange={(e) => handleMainVolumeChange(parseInt(e.target.value))}
                    className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${mainVolume}%, #e5e7eb ${mainVolume}%, #e5e7eb 100%)`
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <button 
                    className="py-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-800/50 transition-colors font-medium"
                    onClick={() => handleVolumePresetClick('mute')}
                  >
                    {t('mute')}
                  </button>
                  <button 
                    className="py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors font-medium"
                    onClick={() => handleVolumePresetClick('medium')}
                  >
                    {t('medium')}
                  </button>
                  <button 
                    className="py-3 bg-primary-100 text-primary-600 rounded-lg hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-400 dark:hover:bg-primary-800/50 transition-colors font-medium"
                    onClick={() => handleVolumePresetClick('max')}
                  >
                    {t('max')}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop: Original layout */}
      <div className="hidden md:block p-4">
        <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200 flex items-center">
          <span className="inline-block w-2 h-6 bg-primary-500 rounded-sm mr-2"></span>
          {t('volumeControl')}
        </h3>
        
        <div className="flex flex-col items-center">
          <div className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">{t('mainVolume')}</div>
          
          <div className="bg-white dark:bg-gray-900 p-2 rounded-lg shadow-sm">
            <VerticalFader 
              value={mainVolume} 
              onChange={handleMainVolumeChange}
              min={0}
              max={100}
              step={1}
              label=""
            />
          </div>
          
          <div className="mt-6 grid grid-cols-3 gap-2 w-full max-w-xs">
            <button 
              className="px-4 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-800/50 transition-colors"
              onClick={() => handleVolumePresetClick('mute')}
            >
              {t('mute')}
            </button>
            <button 
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleVolumePresetClick('medium')}
            >
              {t('medium')}
            </button>
            <button 
              className="px-4 py-2 bg-primary-100 text-primary-600 rounded-md hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-400 dark:hover:bg-primary-800/50 transition-colors"
              onClick={() => handleVolumePresetClick('max')}
            >
              {t('max')}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VolumeControls;